import type { SvgPathSegment } from '../types/svg-path-segment'
import type { SvgPathSubpath } from '../types/svg-path-subpath'

/** Argument count per command letter, used to slice a run of implicit repeats. */
export const ARITY: Record<string, number> = {
  M: 2,
  L: 2,
  H: 1,
  V: 1,
  C: 6,
  S: 4,
  Q: 4,
  T: 2,
  A: 7,
  Z: 0,
}

/**
 * Normalise `d` into absolute `M`/`L`/`C`/`Q`/`A`/`Z` segments.
 *
 * A repeated command (`L 1 2 3 4`) and an implicit-lineto `M` (`M 0 0 1 1`) are both part of the
 * grammar, so arguments are consumed in fixed-size groups rather than one group per letter.
 */
export function parseSegments(d: string): SvgPathSegment[] | null {
  const tokens = d.match(/[MmLlHhVvCcSsQqTtAaZz]|[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g)

  if (!tokens) return null

  const segments: SvgPathSegment[] = []
  // Current point, subpath start, and the previous curve's control point — the last is what the
  // smooth forms (`S`/`T`) reflect to recover their first control point.
  let cx = 0
  let cy = 0
  let startX = 0
  let startY = 0
  let lastControlX: number | null = null
  let lastControlY: number | null = null
  let lastCubic = false
  let lastQuadratic = false

  let i = 0
  let previousLetter: string | null = null

  while (i < tokens.length) {
    let letter = tokens[i]!

    if (/[MmLlHhVvCcSsQqTtAaZz]/.test(letter)) {
      i++
    } else if (previousLetter) {
      // An implicit repeat: reuse the previous letter, except that a repeated `M` continues as `L`
      // per the grammar.
      letter = previousLetter === 'M' ? 'L' : previousLetter === 'm' ? 'l' : previousLetter
    } else {
      return null
    }

    previousLetter = letter
    const upper = letter.toUpperCase()
    const relative = letter !== upper
    const arity = ARITY[upper]

    if (arity === undefined) return null

    const args: number[] = []

    for (let a = 0; a < arity; a++) {
      const token = tokens[i]

      if (token === undefined || /[MmLlHhVvCcSsQqTtAaZz]/.test(token)) return null
      const value = Number(token)

      if (!Number.isFinite(value)) return null
      args.push(value)
      i++
    }

    const isCubic = upper === 'C' || upper === 'S'
    const isQuadratic = upper === 'Q' || upper === 'T'

    switch (upper) {
      case 'M': {
        const x = relative ? cx + args[0]! : args[0]!
        const y = relative ? cy + args[1]! : args[1]!

        segments.push({ type: 'M', x, y })
        cx = startX = x
        cy = startY = y
        break
      }

      case 'L': {
        const x = relative ? cx + args[0]! : args[0]!
        const y = relative ? cy + args[1]! : args[1]!

        segments.push({ type: 'L', x, y })
        cx = x
        cy = y
        break
      }

      case 'H': {
        const x = relative ? cx + args[0]! : args[0]!

        segments.push({ type: 'L', x, y: cy })
        cx = x
        break
      }

      case 'V': {
        const y = relative ? cy + args[0]! : args[0]!

        segments.push({ type: 'L', x: cx, y })
        cy = y
        break
      }

      case 'C': {
        const x1 = relative ? cx + args[0]! : args[0]!
        const y1 = relative ? cy + args[1]! : args[1]!
        const x2 = relative ? cx + args[2]! : args[2]!
        const y2 = relative ? cy + args[3]! : args[3]!
        const x = relative ? cx + args[4]! : args[4]!
        const y = relative ? cy + args[5]! : args[5]!

        segments.push({ type: 'C', x1, y1, x2, y2, x, y })
        lastControlX = x2
        lastControlY = y2
        cx = x
        cy = y
        break
      }

      case 'S': {
        // First control point is the reflection of the previous cubic's second one about the
        // current point; with no previous cubic it coincides with the current point.
        const x1: number = lastCubic && lastControlX != null ? 2 * cx - lastControlX : cx
        const y1: number = lastCubic && lastControlY != null ? 2 * cy - lastControlY : cy
        const x2 = relative ? cx + args[0]! : args[0]!
        const y2 = relative ? cy + args[1]! : args[1]!
        const x = relative ? cx + args[2]! : args[2]!
        const y = relative ? cy + args[3]! : args[3]!

        segments.push({ type: 'C', x1, y1, x2, y2, x, y })
        lastControlX = x2
        lastControlY = y2
        cx = x
        cy = y
        break
      }

      case 'Q': {
        const x1 = relative ? cx + args[0]! : args[0]!
        const y1 = relative ? cy + args[1]! : args[1]!
        const x = relative ? cx + args[2]! : args[2]!
        const y = relative ? cy + args[3]! : args[3]!

        segments.push({ type: 'Q', x1, y1, x, y })
        lastControlX = x1
        lastControlY = y1
        cx = x
        cy = y
        break
      }

      case 'T': {
        const x1: number = lastQuadratic && lastControlX != null ? 2 * cx - lastControlX : cx
        const y1: number = lastQuadratic && lastControlY != null ? 2 * cy - lastControlY : cy
        const x = relative ? cx + args[0]! : args[0]!
        const y = relative ? cy + args[1]! : args[1]!

        segments.push({ type: 'Q', x1, y1, x, y })
        lastControlX = x1
        lastControlY = y1
        cx = x
        cy = y
        break
      }

      case 'A': {
        const x = relative ? cx + args[5]! : args[5]!
        const y = relative ? cy + args[6]! : args[6]!

        segments.push({
          type: 'A',
          rx: Math.abs(args[0]!),
          ry: Math.abs(args[1]!),
          rotation: args[2]!,
          largeArc: args[3]! ? 1 : 0,
          sweep: args[4]! ? 1 : 0,
          x,
          y,
        })
        cx = x
        cy = y
        break
      }

      case 'Z': {
        segments.push({ type: 'Z' })
        cx = startX
        cy = startY
        break
      }

      default:
        return null
    }

    lastCubic = isCubic
    lastQuadratic = isQuadratic
    if (!isCubic && !isQuadratic) {
      lastControlX = null
      lastControlY = null
    }
  }

  return segments.length > 0 ? segments : null
}

export function groupSubpaths(segments: SvgPathSegment[]): SvgPathSubpath[] {
  const subpaths: SvgPathSubpath[] = []
  let current: SvgPathSubpath | null = null

  for (const segment of segments) {
    if (segment.type === 'M') {
      current = { startX: segment.x, startY: segment.y, segments: [], closed: false }
      subpaths.push(current)
      continue
    }

    if (current == null) continue

    if (segment.type === 'Z') {
      current.closed = true
      continue
    }

    current.segments.push(segment)
  }

  return subpaths.filter((s) => s.segments.length > 0)
}

/**
 * Parse `d` into absolute subpaths — the same normalisation the reverser runs on, exposed so a
 * second consumer does not have to stand up a second SVG path grammar.
 *
 * Used by `offset-svg-path.ts`. Kept here rather than moved somewhere neutral because the parser
 * and its `Z`/implicit-repeat/reflection handling are the thing worth having exactly once, and
 * this is where they already live and are already tested.
 */
export function parseSvgPathSubpaths(d: string): SvgPathSubpath[] | null {
  const segments = parseSegments(d)

  if (!segments) return null

  return groupSubpaths(segments)
}
